/**
 * VitalsGraphs - Renders vitals trend charts (Heart Rate, Blood Pressure, Temperature, SpO2).
 *
 * Displays 4 line charts in a 2x2 grid using react-chartjs-2.
 * Each chart features:
 * - Normalized y-axis with buffer (slope is not exaggerated)
 * - Normal range shown as a subtle shaded band (not cluttering the legend)
 * - Clean minimal legend showing only the data series
 * - Smooth curve rendering matching the health-tracker module style
 *
 * Requirements: 17.1
 */

import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { ChartDataSeries, ClusteredVitalsViewModel } from '../types';
import { formatReportTimeGroup } from '../clustering/circularTime';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/** Props for the VitalsGraphs component */
export interface VitalsGraphsProps {
  heartRate?: ChartDataSeries;
  bloodPressure?: ChartDataSeries;
  temperature?: ChartDataSeries;
  spo2?: ChartDataSeries;
  vitalsTable?: VitalsTableRow[];
  clusteredVitals?: ClusteredVitalsViewModel;
}

/** A single row in the vitals summary table */
export interface VitalsTableRow {
  date: string;
  clusterValues: Record<string, string>;
  heartRate: string;
  temperature: string;
  spo2: string;
  weight: string;
}

/** Chart color configuration for each vital type */
const CHART_COLORS = {
  heartRate: {
    borderColor: 'rgba(220, 53, 69, 1)',
    backgroundColor: 'rgba(220, 53, 69, 0.08)',
  },
  bloodPressure: {
    borderColor: 'rgba(90, 70, 170, 1)',
    backgroundColor: 'rgba(90, 70, 170, 0.08)',
  },
  temperature: {
    borderColor: 'rgba(255, 153, 0, 1)',
    backgroundColor: 'rgba(255, 153, 0, 0.08)',
  },
  spo2: {
    borderColor: 'rgba(40, 167, 69, 1)',
    backgroundColor: 'rgba(40, 167, 69, 0.08)',
  },
};

/** Per-dataset color overrides for multi-line charts (e.g., BP systolic/diastolic) */
const DATASET_COLORS: Record<string, { borderColor: string; backgroundColor: string }> = {
  'Systolic': { borderColor: 'rgba(90, 70, 170, 1)', backgroundColor: 'rgba(90, 70, 170, 0.08)' },
  'Diastolic': { borderColor: 'rgba(0, 123, 255, 1)', backgroundColor: 'rgba(0, 123, 255, 0.08)' },
};

/**
 * Normal range definitions for each vital type.
 * Rendered as a subtle green shaded band.
 */
const NORMAL_RANGES: Record<string, { low: number; high: number }> = {
  'Heart Rate': { low: 60, high: 100 },
  'Blood Pressure Systolic': { low: 90, high: 140 },
  'Blood Pressure Diastolic': { low: 60, high: 90 },
  'Temperature': { low: 36.1, high: 37.2 },
  'SpO2': { low: 95, high: 100 },
};

/**
 * Compute min/max from all data values in a ChartDataSeries.
 */
function getDataMinMax(series: ChartDataSeries): { min: number; max: number } {
  const allValues = series.datasets.flatMap(ds => ds.data).filter(v => v !== 0);
  if (allValues.length === 0) return { min: 0, max: 100 };
  return { min: Math.min(...allValues), max: Math.max(...allValues) };
}

/**
 * Calculate y-axis bounds with buffer for normalized slope.
 * Adds padding so the data line sits in the middle zone of the chart.
 * Also includes the normal range for context.
 */
function computeYBounds(
  dataMin: number,
  dataMax: number,
  normalLow?: number,
  normalHigh?: number
): { suggestedMin: number; suggestedMax: number } {
  const effectiveMin = normalLow != null ? Math.min(dataMin, normalLow) : dataMin;
  const effectiveMax = normalHigh != null ? Math.max(dataMax, normalHigh) : dataMax;

  const range = effectiveMax - effectiveMin;
  const buffer = Math.max(range * 0.15, 2);

  return {
    suggestedMin: Math.floor((effectiveMin - buffer) * 10) / 10,
    suggestedMax: Math.ceil((effectiveMax + buffer) * 10) / 10,
  };
}

/**
 * Builds chart data with a clean normal-range band rendered as a
 * filled area between two invisible boundary lines.
 * The legend only shows the actual data series.
 */
function buildChartData(
  series: ChartDataSeries,
  colors: { borderColor: string; backgroundColor: string },
  vitalType: string
) {
  const numPoints = series.labels.length;

  const datasets: any[] = [];

  // ─── Normal range band (rendered first, behind data) ─────────────────────
  if (vitalType === 'Blood Pressure') {
    const sysRange = NORMAL_RANGES['Blood Pressure Systolic'];
    // Shaded band for systolic normal range
    datasets.push({
      label: '_normalBandSysHigh',
      data: Array(numPoints).fill(sysRange.high),
      borderColor: 'transparent',
      backgroundColor: 'rgba(40, 167, 69, 0.06)',
      fill: '+1', // Fill down to next dataset
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 0,
      order: 10,
    });
    datasets.push({
      label: '_normalBandSysLow',
      data: Array(numPoints).fill(sysRange.low),
      borderColor: 'rgba(40, 167, 69, 0.25)',
      backgroundColor: 'transparent',
      borderDash: [4, 4],
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1,
      order: 10,
    });
    // Diastolic normal range band
    const diaRange = NORMAL_RANGES['Blood Pressure Diastolic'];
    datasets.push({
      label: '_normalBandDiaHigh',
      data: Array(numPoints).fill(diaRange.high),
      borderColor: 'rgba(0, 123, 255, 0.2)',
      backgroundColor: 'rgba(0, 123, 255, 0.04)',
      fill: '+1',
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1,
      borderDash: [4, 4],
      order: 10,
    });
    datasets.push({
      label: '_normalBandDiaLow',
      data: Array(numPoints).fill(diaRange.low),
      borderColor: 'rgba(0, 123, 255, 0.2)',
      backgroundColor: 'transparent',
      borderDash: [4, 4],
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1,
      order: 10,
    });
  } else {
    const normalRange = NORMAL_RANGES[vitalType];
    if (normalRange) {
      datasets.push({
        label: '_normalBandHigh',
        data: Array(numPoints).fill(normalRange.high),
        borderColor: 'transparent',
        backgroundColor: 'rgba(40, 167, 69, 0.07)',
        fill: '+1',
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 0,
        order: 10,
      });
      datasets.push({
        label: '_normalBandLow',
        data: Array(numPoints).fill(normalRange.low),
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 0,
        order: 10,
      });
    }
  }

  // ─── Main data series ────────────────────────────────────────────────────
  series.datasets.forEach((ds) => {
    const dsColors = DATASET_COLORS[ds.label] || colors;
    datasets.push({
      label: ds.label,
      data: ds.data,
      borderColor: dsColors.borderColor,
      backgroundColor: dsColors.backgroundColor,
      fill: true,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderColor: dsColors.borderColor,
      pointBorderWidth: 2,
      borderWidth: 2.5,
      order: 1,
    });
  });

  return {
    labels: series.labels,
    datasets,
  };
}

/**
 * Chart options with normalized y-axis bounds and clean styling.
 */
function buildChartOptions(title: string, yAxisLabel: string, series: ChartDataSeries, vitalType: string) {
  const { min: dataMin, max: dataMax } = getDataMinMax(series);

  let normalLow: number | undefined;
  let normalHigh: number | undefined;
  if (vitalType === 'Blood Pressure') {
    normalLow = NORMAL_RANGES['Blood Pressure Diastolic'].low;
    normalHigh = NORMAL_RANGES['Blood Pressure Systolic'].high;
  } else if (NORMAL_RANGES[vitalType]) {
    normalLow = NORMAL_RANGES[vitalType].low;
    normalHigh = NORMAL_RANGES[vitalType].high;
  }

  const { suggestedMin, suggestedMax } = computeYBounds(dataMin, dataMax, normalLow, normalHigh);

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          // Hide the internal normal-band datasets from legend
          filter: (item: any) => {
            return !item.text.startsWith('_');
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          padding: 12,
          font: { size: 11, weight: 500 },
        },
      },
      title: {
        display: true,
        text: title,
        font: { size: 14, weight: 'bold' as const },
        padding: { bottom: 8 },
        color: '#333',
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#555',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        boxPadding: 4,
        usePointStyle: true,
        filter: (item: any) => {
          return !item.dataset.label.startsWith('_');
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          color: '#888',
          font: { size: 11 },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: '#888',
          font: { size: 10 },
          maxRotation: 0,
        },
      },
      y: {
        title: {
          display: true,
          text: yAxisLabel,
          color: '#888',
          font: { size: 11 },
        },
        suggestedMin,
        suggestedMax,
        grid: {
          color: 'rgba(0, 0, 0, 0.04)',
          drawBorder: false,
        },
        ticks: {
          color: '#888',
          font: { size: 10 },
          padding: 8,
        },
        border: {
          display: false,
        },
      },
    },
  };
}

/**
 * Renders a single vital chart card.
 */
const VitalChart: React.FC<{
  title: string;
  yAxisLabel: string;
  data: ChartDataSeries;
  colors: { borderColor: string; backgroundColor: string };
  vitalType: string;
  normalLabel?: string;
}> = ({ title, yAxisLabel, data, colors, vitalType, normalLabel }) => {
  const chartData = buildChartData(data, colors, vitalType);
  const options = buildChartOptions(title, yAxisLabel, data, vitalType);

  const normalRange = vitalType === 'Blood Pressure'
    ? NORMAL_RANGES['Blood Pressure Systolic']
    : NORMAL_RANGES[vitalType];

  return (
    <div
      className="vital-chart-container"
      style={{
        padding: '1rem',
        border: '1px solid #f0f0f0',
        borderRadius: '12px',
        backgroundColor: '#fff',
        height: '320px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'relative',
      }}
    >
      <Line data={chartData} options={options} />
      {/* Normal range label */}
      {normalRange && (
        <div style={{
          position: 'absolute',
          bottom: '8px',
          right: '12px',
          fontSize: '9px',
          color: '#888',
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '2px 6px',
          borderRadius: '4px',
        }}>
          Normal: {normalLabel || `${normalRange.low}–${normalRange.high} ${yAxisLabel}`}
        </div>
      )}
    </div>
  );
};

/**
 * VitalsGraphs component - renders vitals trend charts in a 2x2 grid.
 */
const VitalsGraphs: React.FC<VitalsGraphsProps> = ({
  heartRate,
  bloodPressure,
  temperature,
  spo2,
  vitalsTable,
  clusteredVitals,
}) => {
  const realSeries = (series?: ChartDataSeries): series is ChartDataSeries =>
    Boolean(series?.datasets.some(ds => ds.data.some(value => Number.isFinite(value) && value !== 0)));
  const hasData = realSeries(heartRate) || realSeries(temperature) || realSeries(spo2) || clusteredVitals?.bloodPressure.hasData || clusteredVitals?.glucose.visible;
  const clusterColumns = Array.from(new Set((vitalsTable || []).flatMap(row => Object.keys(row.clusterValues || {}))));

  return (
    <div className="vitals-graphs" style={{ padding: '1rem' }}>
      {hasData && (
      <div
        className="vitals-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        {realSeries(heartRate) && (
          <VitalChart
            title="Heart Rate"
            yAxisLabel="BPM"
            data={heartRate}
            colors={CHART_COLORS.heartRate}
            vitalType="Heart Rate"
            normalLabel="60–100 bpm"
          />
        )}
        {clusteredVitals?.bloodPressure.graphs.map(graph => (
          <VitalChart
            key={`bp-${graph.representativeTime}`}
            title={`Blood Pressure — ${formatReportTimeGroup(graph.representativeMinutes)}`}
            yAxisLabel="mmHg"
            data={graph}
            colors={CHART_COLORS.bloodPressure}
            vitalType="Blood Pressure"
            normalLabel="90–140 / 60–90 mmHg"
          />
        ))}
        {clusteredVitals?.glucose.visible && clusteredVitals.glucose.graphs.map(graph => (
          <VitalChart key={`glucose-${graph.representativeTime}`} title={`Glucose — ${formatReportTimeGroup(graph.representativeMinutes)}`} yAxisLabel="mg/dL" data={graph} colors={CHART_COLORS.bloodPressure} vitalType="Glucose" />
        ))}
        {realSeries(temperature) && (
          <VitalChart
            title="Temperature"
            yAxisLabel="°C"
            data={temperature}
            colors={CHART_COLORS.temperature}
            vitalType="Temperature"
            normalLabel="36.1–37.2 °C"
          />
        )}
        {realSeries(spo2) && (
          <VitalChart
            title="SpO2"
            yAxisLabel="%"
            data={spo2}
            colors={CHART_COLORS.spo2}
            vitalType="SpO2"
            normalLabel="95–100 %"
          />
        )}
      </div>
      )}

      {!hasData && (
        <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#fafafa', borderRadius: '12px' }}>
          <p style={{ color: '#999', fontStyle: 'italic', margin: 0 }}>
            No vital data recorded for this period.
          </p>
        </div>
      )}
      {clusteredVitals && !clusteredVitals.bloodPressure.hasData && (
        <div role="status" style={{padding:'1rem',color:'#666'}}>No blood-pressure data available for this reporting period.</div>
      )}

      {/* Vitals Summary Table */}
      {vitalsTable && vitalsTable.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h4 style={{ fontSize: '1rem', color: '#333', marginBottom: '0.75rem', fontWeight: 600 }}>Vital Signs Record</h4>
          <div style={{ borderRadius: '8px', overflowX: 'auto', border: '1px solid #e6e1f2' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}
              aria-label="Vital signs table"
            >
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  {['Date', ...clusterColumns, 'Heart Rate (bpm)', 'Temp (°C)', 'SpO2 (%)', 'Weight (kg)'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        padding: '10px 14px',
                        textAlign: 'left',
                        borderBottom: '1px solid #eee',
                        color: '#666',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vitalsTable.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                  >
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444', fontWeight: 500 }}>{row.date}</td>
                    {clusterColumns.map(column => <td key={column} style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444', whiteSpace: 'nowrap' }}>{row.clusterValues[column] || '—'}</td>)}
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444' }}>{row.heartRate}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444' }}>{row.temperature}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444' }}>{row.spo2}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #f5f5f5', color: '#444' }}>{row.weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VitalsGraphs;
